import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { organizationsAPI, metricsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
    BarChart3,
    Building2,
    Users,
    Briefcase,
    TrendingUp,
    Award,
    Loader2,
    CheckCircle,
    Clock,
    ChevronRight,
    History,
    FileText,
    DollarSign,
    Handshake,
    GraduationCap,
    Target,
} from 'lucide-react';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

const SubmitMetricsPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading, login } = useAuth();
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [existingMetrics, setExistingMetrics] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(true);
    const [loadingMetrics, setLoadingMetrics] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [form, setForm] = useState({
        year: currentYear.toString(),
        people_trained_current_year: '',
        people_trained_all_time: '',
        people_employed_current_year: '',
        people_employed_all_time: '',
        completion_rate: '',
        placement_rate: '',
        employers_engaged: '',
        avg_starting_salary: '',
        notes: '',
    });

    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            fetchMyOrganizations();
        }
    }, [authLoading, isAuthenticated]);

    const fetchMyOrganizations = async () => {
        setLoadingOrgs(true);
        try {
            const response = await organizationsAPI.getMyOrganizations();
            setOrganizations(response.data || []);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load your organizations');
        } finally {
            setLoadingOrgs(false);
        }
    };

    const emptyForm = (year) => ({
        year: year || currentYear.toString(),
        people_trained_current_year: '',
        people_trained_all_time: '',
        people_employed_current_year: '',
        people_employed_all_time: '',
        completion_rate: '',
        placement_rate: '',
        employers_engaged: '',
        avg_starting_salary: '',
        notes: '',
    });

    const handleSelectOrg = async (org) => {
        setSelectedOrg(org);
        setSubmitted(false);
        setForm(emptyForm());
        setLoadingMetrics(true);
        try {
            const response = await metricsAPI.getOrgMetrics(org.resource_id);
            setExistingMetrics(response.data.metrics || []);
        } catch {
            setExistingMetrics([]);
        } finally {
            setLoadingMetrics(false);
        }
    };

    const handleYearChange = (year) => {
        setSubmitted(false);
        const existing = existingMetrics.find(m => m.year === parseInt(year));
        if (existing) {
            setForm({
                year,
                people_trained_current_year: existing.people_trained_current_year?.toString() || '',
                people_trained_all_time: existing.people_trained_all_time?.toString() || '',
                people_employed_current_year: existing.people_employed_current_year?.toString() || '',
                people_employed_all_time: existing.people_employed_all_time?.toString() || '',
                completion_rate: existing.completion_rate?.toString() || '',
                placement_rate: existing.placement_rate?.toString() || '',
                employers_engaged: existing.employers_engaged?.toString() || '',
                avg_starting_salary: existing.avg_starting_salary?.toString() || '',
                notes: existing.notes || '',
            });
        } else {
            setForm(emptyForm(year));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedOrg) return;

        const payload = {
            year: parseInt(form.year),
            people_trained_current_year: form.people_trained_current_year ? parseInt(form.people_trained_current_year) : null,
            people_trained_all_time: form.people_trained_all_time ? parseInt(form.people_trained_all_time) : null,
            people_employed_current_year: form.people_employed_current_year ? parseInt(form.people_employed_current_year) : null,
            people_employed_all_time: form.people_employed_all_time ? parseInt(form.people_employed_all_time) : null,
            completion_rate: form.completion_rate ? parseFloat(form.completion_rate) : null,
            placement_rate: form.placement_rate ? parseFloat(form.placement_rate) : null,
            employers_engaged: form.employers_engaged ? parseInt(form.employers_engaged) : null,
            avg_starting_salary: form.avg_starting_salary ? parseFloat(form.avg_starting_salary) : null,
            notes: form.notes || null,
        };

        const hasAtLeastOne = payload.people_trained_current_year || payload.people_trained_all_time ||
            payload.people_employed_current_year || payload.people_employed_all_time ||
            payload.completion_rate || payload.placement_rate ||
            payload.employers_engaged || payload.avg_starting_salary;

        if (!hasAtLeastOne) {
            toast.error('Please fill in at least one metric');
            return;
        }

        setSubmitting(true);
        try {
            await metricsAPI.submitMetrics(selectedOrg.resource_id, payload);
            toast.success('Metrics submitted successfully! An admin will review your submission.');
            setSubmitted(true);
            const response = await metricsAPI.getOrgMetrics(selectedOrg.resource_id);
            setExistingMetrics(response.data.metrics || []);
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to submit metrics';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const existingForYear = existingMetrics.find(m => m.year === parseInt(form.year));

    if (authLoading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="max-w-3xl mx-auto px-4 py-12">
                    <Skeleton className="h-10 w-64 mb-4" />
                    <Skeleton className="h-6 w-96 mb-8" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center" data-testid="metrics-login-required">
                <Card className="max-w-md w-full mx-4">
                    <CardContent className="pt-8 pb-8 text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-4 text-primary" />
                        <h2 className="text-xl font-semibold mb-2">Member Login Required</h2>
                        <p className="text-muted-foreground mb-6">
                            You need to be logged in as a member to submit annual impact metrics for your organization.
                        </p>
                        <Button onClick={login} data-testid="metrics-login-btn">
                            Member Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="submit-metrics-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                        Annual Impact Metrics
                    </h1>
                    <p className="text-muted-foreground text-base">
                        Report your organization's annual impact data. Submissions are reviewed by HAAPE administrators.
                    </p>
                </div>

                {/* Step 1: Select Organization */}
                {!selectedOrg ? (
                    <Card data-testid="org-selection-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Building2 className="h-5 w-5 text-primary" />
                                Select Your Organization
                            </CardTitle>
                            <CardDescription>
                                Choose the organization you'd like to submit metrics for.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loadingOrgs ? (
                                <div className="space-y-3">
                                    {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                                </div>
                            ) : organizations.length === 0 ? (
                                <div className="text-center py-8" data-testid="no-orgs-message">
                                    <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                                    <p className="text-muted-foreground mb-1">No organizations found.</p>
                                    <p className="text-sm text-muted-foreground">
                                        You need to be the creator or an editor of an organization to submit metrics.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {organizations.map((org) => (
                                        <button
                                            key={org.resource_id}
                                            onClick={() => handleSelectOrg(org)}
                                            className="w-full p-4 border border-border rounded-lg text-left hover:bg-muted/50 hover:border-primary/30 transition-all flex items-center gap-4 group"
                                            data-testid={`org-select-${org.resource_id}`}
                                        >
                                            {org.organization_pic ? (
                                                <img src={org.organization_pic} alt="" className="h-10 w-10 rounded-md object-cover border" />
                                            ) : (
                                                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                                                    <Building2 className="h-5 w-5 text-primary" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{org.name}</p>
                                                <p className="text-sm text-muted-foreground">{org.city ? `${org.city}, ` : ''}{org.state}</p>
                                            </div>
                                            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* Selected Org Header */}
                        <Card className="mb-6 bg-primary/5 border-primary/20" data-testid="selected-org-header">
                            <CardContent className="py-4">
                                <div className="flex items-center gap-4">
                                    {selectedOrg.organization_pic ? (
                                        <img src={selectedOrg.organization_pic} alt="" className="h-12 w-12 rounded-md object-cover border" />
                                    ) : (
                                        <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center">
                                            <Building2 className="h-6 w-6 text-primary" />
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <p className="font-semibold">{selectedOrg.name}</p>
                                        <p className="text-sm text-muted-foreground">{selectedOrg.city ? `${selectedOrg.city}, ` : ''}{selectedOrg.state}</p>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => { setSelectedOrg(null); setSubmitted(false); }} data-testid="change-org-btn">
                                        Change
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Submission Form */}
                        {submitted ? (
                            <Card data-testid="submission-success">
                                <CardContent className="py-12 text-center">
                                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                                    <h3 className="text-xl font-semibold mb-2">Metrics Submitted</h3>
                                    <p className="text-muted-foreground mb-6">
                                        Your {form.year} impact metrics for {selectedOrg.name} have been submitted for review.
                                    </p>
                                    <div className="flex gap-3 justify-center">
                                        <Button variant="outline" onClick={() => setSubmitted(false)} data-testid="submit-another-btn">
                                            Submit for Another Year
                                        </Button>
                                        <Button onClick={() => navigate(`/resource/${selectedOrg.resource_id}`)} data-testid="view-org-btn">
                                            View Organization
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card data-testid="metrics-form-card">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <FileText className="h-5 w-5 text-primary" />
                                        Submit Impact Data
                                    </CardTitle>
                                    <CardDescription>
                                        Enter your organization's impact numbers for the selected year.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        {/* Year Selector */}
                                        <div className="space-y-2">
                                            <Label htmlFor="year">Reporting Year</Label>
                                            <Select value={form.year} onValueChange={handleYearChange}>
                                                <SelectTrigger data-testid="year-select">
                                                    <SelectValue placeholder="Select year" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {yearOptions.map(y => (
                                                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {existingForYear && (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Badge variant={existingForYear.status === 'approved' ? 'default' : existingForYear.status === 'rejected' ? 'destructive' : 'secondary'}>
                                                        {existingForYear.status}
                                                    </Badge>
                                                    <span className="text-muted-foreground">
                                                        Previously submitted — updating will overwrite
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* People Trained */}
                                        <div>
                                            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                                                <GraduationCap className="h-4 w-4" />
                                                People Trained
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="people_trained_current_year">Current Year</Label>
                                                    <Input
                                                        id="people_trained_current_year"
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 150"
                                                        value={form.people_trained_current_year}
                                                        onChange={e => setForm(prev => ({ ...prev, people_trained_current_year: e.target.value }))}
                                                        data-testid="input-trained-current"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="people_trained_all_time">All Time</Label>
                                                    <Input
                                                        id="people_trained_all_time"
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 1200"
                                                        value={form.people_trained_all_time}
                                                        onChange={e => setForm(prev => ({ ...prev, people_trained_all_time: e.target.value }))}
                                                        data-testid="input-trained-alltime"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* People Employed */}
                                        <div>
                                            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                                                <Briefcase className="h-4 w-4" />
                                                People Employed
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="people_employed_current_year">Current Year</Label>
                                                    <Input
                                                        id="people_employed_current_year"
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 75"
                                                        value={form.people_employed_current_year}
                                                        onChange={e => setForm(prev => ({ ...prev, people_employed_current_year: e.target.value }))}
                                                        data-testid="input-employed-current"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="people_employed_all_time">All Time</Label>
                                                    <Input
                                                        id="people_employed_all_time"
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 500"
                                                        value={form.people_employed_all_time}
                                                        onChange={e => setForm(prev => ({ ...prev, people_employed_all_time: e.target.value }))}
                                                        data-testid="input-employed-alltime"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Program Rates */}
                                        <div>
                                            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                                                <Target className="h-4 w-4" />
                                                Program Success Rates
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="completion_rate" className="flex items-center gap-1.5">
                                                        <Award className="h-3.5 w-3.5 text-muted-foreground" />
                                                        Completion Rate (%)
                                                    </Label>
                                                    <Input
                                                        id="completion_rate"
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        placeholder="e.g., 85.5"
                                                        value={form.completion_rate}
                                                        onChange={e => setForm(prev => ({ ...prev, completion_rate: e.target.value }))}
                                                        data-testid="input-completion-rate"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="placement_rate" className="flex items-center gap-1.5">
                                                        <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                                                        Placement Rate (%)
                                                    </Label>
                                                    <Input
                                                        id="placement_rate"
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        placeholder="e.g., 72.0"
                                                        value={form.placement_rate}
                                                        onChange={e => setForm(prev => ({ ...prev, placement_rate: e.target.value }))}
                                                        data-testid="input-placement-rate"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Employer & Salary */}
                                        <div>
                                            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                                                <Handshake className="h-4 w-4" />
                                                Employment Details
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="employers_engaged" className="flex items-center gap-1.5">
                                                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                        Employers Engaged
                                                    </Label>
                                                    <Input
                                                        id="employers_engaged"
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 25"
                                                        value={form.employers_engaged}
                                                        onChange={e => setForm(prev => ({ ...prev, employers_engaged: e.target.value }))}
                                                        data-testid="input-employers-engaged"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="avg_starting_salary" className="flex items-center gap-1.5">
                                                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                                                        Avg Starting Salary ($)
                                                    </Label>
                                                    <Input
                                                        id="avg_starting_salary"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        placeholder="e.g., 35000"
                                                        value={form.avg_starting_salary}
                                                        onChange={e => setForm(prev => ({ ...prev, avg_starting_salary: e.target.value }))}
                                                        data-testid="input-avg-salary"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Notes */}
                                        <div className="space-y-2">
                                            <Label htmlFor="notes">Additional Notes (optional)</Label>
                                            <Textarea
                                                id="notes"
                                                placeholder="Any context or details about your metrics..."
                                                rows={3}
                                                value={form.notes}
                                                onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                                                data-testid="input-notes"
                                            />
                                        </div>

                                        <Button type="submit" className="w-full sm:w-auto gap-2" disabled={submitting} data-testid="submit-metrics-btn">
                                            {submitting ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <BarChart3 className="h-4 w-4" />
                                            )}
                                            {existingForYear ? 'Update Metrics' : 'Submit Metrics'}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        )}

                        {/* Existing Metrics History */}
                        {existingMetrics.length > 0 && (
                            <Card className="mt-6" data-testid="metrics-history-card">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <History className="h-5 w-5 text-primary" />
                                        Submission History
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {loadingMetrics ? (
                                        <Skeleton className="h-24 w-full" />
                                    ) : (
                                        <div className="space-y-3">
                                            {existingMetrics.map((m) => (
                                                <div
                                                    key={m.metrics_id}
                                                    className="p-3 border border-border rounded-lg flex flex-col sm:flex-row sm:items-center gap-3"
                                                    data-testid={`history-${m.metrics_id}`}
                                                >
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <span className="font-mono font-semibold text-lg">{m.year}</span>
                                                        <Badge variant={m.status === 'approved' ? 'default' : m.status === 'rejected' ? 'destructive' : 'secondary'}>
                                                            {m.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                                                            {m.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                                                            {m.status}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                                        {m.people_trained_current_year != null && <span>Trained: <strong className="text-foreground">{m.people_trained_current_year}</strong></span>}
                                                        {m.people_employed_current_year != null && <span>Employed: <strong className="text-foreground">{m.people_employed_current_year}</strong></span>}
                                                        {m.completion_rate != null && <span>Completion: <strong className="text-foreground">{m.completion_rate}%</strong></span>}
                                                        {m.placement_rate != null && <span>Placement: <strong className="text-foreground">{m.placement_rate}%</strong></span>}
                                                        {m.employers_engaged != null && <span>Employers: <strong className="text-foreground">{m.employers_engaged}</strong></span>}
                                                        {m.avg_starting_salary != null && <span>Salary: <strong className="text-foreground">${m.avg_starting_salary.toLocaleString()}</strong></span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SubmitMetricsPage;
